import * as yup from 'yup';
import { ERROR_MESSAGES } from '@/shared/constants/error-messages';
import { useAppSelector } from '@/shared/hooks/store';
import { resetLoading } from '@/store/slices/auth/selectors';
import { confirmPasswordValidation, passwordValidation } from '@/shared/helpers/validations';
import { charLimitRegex, digitRegex, spaceRegex, specialCharRegex, textLowerCaseRegex, textUpperCaseRegex } from '@/shared/constants/regex';

// Components
import { Alert, Input } from '@/shared/ui';
import Button from '@/shared/ui/button';
import { Formik } from 'formik';

interface Props {
    handleChangeStep: (step: number, data: { pass: string }) => void;
}
export const regexAndErrorMessageMapping = [
    { regex: charLimitRegex, errorMessage: ERROR_MESSAGES.PASSWORD_CHAR_LIMIT },
    { regex: textLowerCaseRegex, errorMessage: ERROR_MESSAGES.PASSWORD_LOWERCASE },
    { regex: textUpperCaseRegex, errorMessage: ERROR_MESSAGES.PASSWORD_UPPERCASE },
    { regex: digitRegex, errorMessage: ERROR_MESSAGES.PASSWORD_DIGIT },
    { regex: spaceRegex, errorMessage: ERROR_MESSAGES.PASSWORD_NO_SPACES },
    { regex: specialCharRegex, errorMessage: ERROR_MESSAGES.PASSWORD_SPECIAL_CHAR }
];
const initialValues = { pass: '', confirmPassword: '' };

const ConfirmPassword = ({ handleChangeStep }: Props) => {
    const loading = useAppSelector(resetLoading);
    const validationSchema = yup.object().shape({
        pass: passwordValidation,
        confirmPassword: confirmPasswordValidation('pass')
    });

    return (
        <div className="py-2.5" data-testid="confirm-password-container">
            <h2 className="text-2xl xs:text-4xl lg:leading-[49px] font-normal mb-7 text-center mx-auto font-agrandir" data-testid="confirm-password-reset-title">
                Please reset your <br /> password
            </h2>

            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={(values): void => {
                    handleChangeStep(4, { pass: values.pass });
                }}
            >
                {({ values, errors, touched, handleSubmit, setFieldValue }) => {
                    const validationRules: string[] = [];
                    regexAndErrorMessageMapping.forEach(item => {
                        const isValid = item.regex.test(values.pass);
                        if (!isValid) validationRules.push(item.errorMessage);
                    });

                    return (
                        <div className="flex flex-col gap-6">
                            <div className="mb-1 flex flex-col gap-2.5 lg:gap-6">
                                <Input
                                    name="pass"
                                    type="password"
                                    label="New Password"
                                    value={values.pass}
                                    required
                                    placeholder="•••••••••"
                                    error={!!(touched.pass && errors.pass)}
                                    onChange={event => setFieldValue('pass', event.target.value)}
                                    testId="new-password"
                                />
                                {touched.pass && errors.pass && (
                                    <Alert label="Password must contain:">
                                        <ul className="list-disc pl-4 text-sm lg:text-lg flex flex-col gap-2">
                                            {validationRules.map((e, index) => (
                                                <li className="lg:text-lg text-base font-dmSans" key={index}>
                                                    {e}
                                                </li>
                                            ))}
                                        </ul>
                                    </Alert>
                                )}
                            </div>

                            <Input
                                name="confirmPassword"
                                type="password"
                                label="Confirm Password"
                                value={values.confirmPassword}
                                required
                                placeholder="•••••••••"
                                error={!!(touched.confirmPassword && errors.confirmPassword)}
                                errorMessage={errors.confirmPassword}
                                onChange={event => setFieldValue('confirmPassword', event.target.value)}
                                testId="confirm-password"
                            />

                            <div className="w-full flex flex-col gap-4 items-start">
                                <Button
                                    sx={{ width: '100%' }}
                                    disabled={loading}
                                    onClick={() => handleSubmit()}
                                    fontClassName="text-base lg-max:py-0.5"
                                    showEndIcon
                                    testId="button-update-password"
                                >
                                    Update Password
                                </Button>
                            </div>
                        </div>
                    );
                }}
            </Formik>
        </div>
    );
};

export default ConfirmPassword;
